import axios from "axios";
import { Message } from "element-ui";
import router from "@/router";
import { clearUserInfo } from "./userInfo";
import { clearDictCache } from "./dict";
import { clearCachedMenus } from "./dynamicMenu";
import { resetDynamicRoutes } from "./dynamicRoutes";
import { clearUserPermissions } from "./permission";
import settings from "@/settings";
import { getToken, clearToken } from "@/utils/token";

axios.defaults.baseURL = settings.baseApi;
axios.defaults.timeout = 100000;

const Axios = axios.create({
  baseURL: settings.baseApi,
  timeout: 100000
});

let isRedirectingToLogin = false;

const showApiError = msg => {
  Message.error(msg || "接口错误");
};

/** 从本地读取 token 并写入请求头 Authorization */
export function applyAuthHeader(config = {}) {
  const token = getToken();
  if (!token) return config;

  const headers = config.headers || {};
  if (typeof headers.set === "function") {
    headers.set("Authorization", token);
    config.headers = headers;
  } else {
    config.headers = {
      ...headers,
      Authorization: token
    };
  }
  return config;
}

function handleUnauthorized(msg) {
  clearToken();
  clearUserInfo();
  clearDictCache();
  clearCachedMenus();
  clearUserPermissions();
  resetDynamicRoutes();
  if (router.currentRoute.path === "/login") {
    return;
  }
  if (isRedirectingToLogin) {
    return;
  }
  isRedirectingToLogin = true;
  showApiError(msg || "登录已过期，请重新登录");
  router.replace({ path: "/login" }).catch(() => {});
  setTimeout(() => {
    isRedirectingToLogin = false;
  }, 1000);
}

function isAuthErrorCode(code) {
  return code == 401 || code == 402 || code == 403;
}

function handleBlobError(blob) {
  return blob.text().then(text => {
    try {
      const result = JSON.parse(text);
      if (isAuthErrorCode(result.code)) {
        handleUnauthorized(result.msg);
        return Promise.reject(result);
      }
      showApiError(result.msg || "接口错误");
      return Promise.reject(result);
    } catch (e) {
      showApiError("接口错误");
      return Promise.reject(e);
    }
  });
}

Axios.interceptors.response.use(
  res => {
    if (res.config.responseType === "blob") {
      const blob = res.data;
      if (blob instanceof Blob && blob.type.includes("application/json")) {
        return handleBlobError(blob);
      }
      return res;
    }
    if (res.status === 200) {
      const skipAuthRedirect = !!(res.config && res.config.skipAuthRedirect);
      if (isAuthErrorCode(res.data.code)) {
        if (skipAuthRedirect) {
          showApiError(res.data.msg);
          return Promise.reject(res.data);
        }
        handleUnauthorized(res.data.msg);
        return Promise.reject(res.data);
      }
      if (res.data.code === 0 || res.data.code === 200) {
        return res.data;
      }
      showApiError(res.data.msg);
      return Promise.reject(res.data);
    }
    showApiError(res.data && res.data.msg);
    return Promise.reject(res);
  },
  error => {
    const response = error.response;
    const data = response && response.data;
    const status = response && response.status;
    const skipAuthRedirect = !!(error.config && error.config.skipAuthRedirect);
    if (
      status === 401 ||
      status === 403 ||
      (data && isAuthErrorCode(data.code))
    ) {
      if (skipAuthRedirect) {
        showApiError((data && data.msg) || "请求失败");
        return Promise.reject(error);
      }
      handleUnauthorized((data && data.msg) || "登录已过期，请重新登录");
      return Promise.reject(error);
    }
    showApiError(error.message);
    return Promise.reject(error);
  }
);

Axios.interceptors.request.use(
  config => applyAuthHeader(config),
  error => {
    showApiError();
    return Promise.reject(error);
  }
);

export { Axios };



// WEBPACK FOOTER //
// ./src/utils/axios.js